import { useCallback, useEffect, useRef, useState } from "react";
import { createUseStyles } from "$utils/createUseStyles";
import { useQuery } from "@tanstack/react-query";
import { CompanyServices, EntrepotService } from "$services";
import { Company, Warehouse } from "$types";
import { theme } from "$utils/theme/bataillog";
import getCompanyLastFile from "$services/File.service";
import useAuth from "$hooks/useAuth";
import { useJobs } from "$hooks/useJobs";
import { queryClient } from "$utils/queryClient";
import { CacheInvalidationAction } from "$utils/cache";
import { ImportType } from "$types/import/ImportType";
import { isJobSuccessful, JobType } from "$types/Job";

const useCompanyStock = (companyId: string, contributor?: Company) => {
  const { isAdmin, token } = useAuth();
  const { startJob, jobs } = useJobs();
  const pendingImportId = useRef<string | null>(null);
  const classes = useStyles();

  // Removed tableData state - DataTable handles the display
  const [selectedEntrepotFile, setSelectedEntrepotFile] =
    useState<any>(undefined);
  const [dataImport, setDataImport] = useState<any>(undefined);
  const [importError, setImportError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedEntrepotId, setSelectedEntrepotId] = useState<string>("");
  const [exportImportType, setExportImportType] = useState<
    "entrepotExport" | "massImport"
  >("entrepotExport");
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [companyLastFile, setCompanyLastFile] = useState<any>(undefined);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);

  const { data: company } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    queryFn: () => CompanyServices.getCompanyById(companyId),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  const fecthWarehouses = useCallback(async () => {
    const filters = {
      ...(contributor
        ? { company: contributor._id }
        : companyId
          ? { company: companyId }
          : {}),
    };

    const queryKey = [CacheInvalidationAction.GET_ENTREPOTS, filters];
    let getWarehouses: any = await queryClient.getQueryData(queryKey);
    if (!getWarehouses) {
      getWarehouses = await queryClient.fetchQuery({
        queryKey,
        queryFn: () => EntrepotService.getEntrepots(filters),
        gcTime: 5 * 60 * 1000,
      });
      queryClient.setQueryData(queryKey, getWarehouses);
    }

    setFilteredWarehouses(
      (getWarehouses?.data || [])?.filter((w: Warehouse) => {
        const isManageable = w.isManageable !== false; // warehouses without "isManageable must be displayed too
        if (contributor) {
          return w.parentCompany === contributor._id && isManageable;
        }
        return (
          companyId === w.parentCompany ||
          w.companies.some(
            (entrepotCompany) =>
              companyId === entrepotCompany._id && entrepotCompany.isManageable
          )
        );
      })
    );
  }, [contributor, companyId]);

  useEffect(() => {
    fecthWarehouses();
  }, [fecthWarehouses]);

  const handleSelectEntrepot = useCallback(
    (id: string) => {
      if (!isAdmin()) return;
      setExportImportType("entrepotExport");
      setSelectedEntrepotId(id);
      setIsModalOpen(true);
    },
    [isAdmin]
  );

  const importStock = async (
    _data: any[],
    csv: { file?: File },
    entrepotId: string,
    targetCompanyId: string
  ) => {
    const file = csv?.file;
    if (!file) {
      throw new Error("Fichier d'import manquant.");
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    const record = await startJob({
      type: JobType.IMPORT_BULK,
      label: "Import stocks",
      params: {
        importType: ImportType.STOCK,
        contextId: targetCompanyId,
        ...(entrepotId ? { warehouseId: entrepotId } : {}),
        originalFileName: file.name,
        fileByteArray: Array.from(bytes),
      },
    });
    pendingImportId.current = record.id;
    return record;
  };

  const onMassImportClick = useCallback(() => {
    setExportImportType("massImport");
    setSelectedEntrepotId("");
    setIsModalOpen(true);
  }, []);

  const handleCallbackImport = useCallback(async () => {
    const companyLastFileData = await getCompanyLastFile(token, companyId);
    setCompanyLastFile(companyLastFileData?.data?.data);
    await queryClient.invalidateQueries({
      queryKey: [CacheInvalidationAction.GET_PRODUCT_STOCK],
    });
    await queryClient.invalidateQueries({
      queryKey: [CacheInvalidationAction.GET_ENTREPOTS],
    });
  }, [companyId, token]);

  useEffect(() => {
    const pendingId = pendingImportId.current;
    if (!pendingId) return;
    const job = jobs.find((item) => item.id === pendingId);
    if (!job || !isJobSuccessful(job)) return;
    pendingImportId.current = null;
    handleCallbackImport();
  }, [jobs, handleCallbackImport]);

  // Update company last file
  useEffect(() => {
    (async () => {
      if (companyId) {
        const companyLastFileData = await getCompanyLastFile(token, companyId);
        setCompanyLastFile(companyLastFileData?.data?.data);
      }
    })();
  }, [companyId, token]);

  // Removed table update useEffect - DataTable handles the data display

  // Update entrepot files - only set the selected entrepot file
  useEffect(() => {
    const entrepotSel = filteredWarehouses?.find(
      (e: any) => e._id === selectedEntrepotId
    );

    if (entrepotSel) {
      if (entrepotSel.importedFiles)
        entrepotSel.importedFiles = entrepotSel.importedFiles.filter(
          (imF: any) => imF.type === "single"
        );
      setSelectedEntrepotFile(entrepotSel);
    } else {
      setSelectedEntrepotFile(undefined);
    }
  }, [selectedEntrepotId, filteredWarehouses]);

  return {
    classes,
    companyLastFile,
    dataImport,
    exportImportType,
    handleCallbackImport,
    handleSelectEntrepot,
    importError,
    importStock,
    isLoading,
    isModalOpen,
    onMassImportClick,
    companyId,
    selectedCompany: company,
    selectedEntrepotFile,
    selectedEntrepotId,
    setDataImport,
    setExportImportType,
    setImportError,
    setIsLoading,
    setIsModalOpen,
    setSelectedEntrepotFile,
  };
};

const useStyles = createUseStyles(() => ({
  btnRight: {
    "&.MuiButton-root": {
      marginLeft: theme.spacing(2),
    },
  },
  entrepotHead: {
    display: "flex",
    "& svg": {
      marginLeft: "5px",
      opacity: 0,
    },
    "&.admin:hover svg": {
      opacity: 1,
    },
    "&.admin": {
      cursor: "pointer",
      color: theme.color.red,
    },
    "&.admin.actual": {
      color: theme.color.green,
    },
  },
  modal: {
    display: "flex",
    padding: theme.spacing(1),
    alignItems: "center",
    justifyContent: "center",
  },
  noResultPlaceHolder: {
    minHeight: "420px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paperModal: {
    flex: "0 1 500px",
    width: 500,
    maxWidth: "100%",
  },
  fileDownloader: {
    alignItems: "center",
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  filename: {
    flex: 1,
  },
  rowStock: {
    display: "flex",
    flex: 1,
  },
  rowStockEntrepot: {
    marginRight: theme.spacing(4),
  },
}));

export default useCompanyStock;
